/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useContext, useState } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { Modal, ActionButton, BrandColors, Text } from '@blend/enterprise-components';

import { updateTitleOrder } from 'blb-api/title';
import { SubjectContext } from 'blb-contexts/Subject';
import { ModalContent } from 'blb-components/ModalContent';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';

import { TitleContext } from './TitleTab';

const styles = StyleSheet.create({
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 24,
  },
  cancelButtonWrapper: {
    margin: '0 12px',
  },
  orderConfirmationCard: {
    backgroundColor: 'white',
    border: `1px solid ${BrandColors.CLOUD_30}`,
    borderRadius: 3,
    padding: '30px 20px',
    marginBottom: 30,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

export const OrderConfirmationCard: React.FC = () => {
  const { setTitleOrder } = useContext(TitleContext);
  // this component only gets rendered if order is present, can assert that it exists
  const order = useContext(TitleContext).order!;
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const subjectContext = useContext(SubjectContext);
  const agencyName = subjectContext?.agency?.name || 'the agency';
  const Toaster = useToasts();

  const handleCloseModal = () => setIsModalOpen(false);

  const confirmTitleOrder = async () => {
    try {
      const update = {
        isConfirmedByLender: true,
      } as Title.Orders.PatchBody; // TODO: update title types to include new PatchBody
      setIsSubmitting(true);
      await updateTitleOrder(order.id, order.tenant, update);
      handleCloseModal();
      setIsSubmitting(false);
      // set it locally after, so it doesn't unmount this component
      setTitleOrder({ ...order, ...update });
    } catch {
      handleCloseModal();
      setIsSubmitting(false);
      Toaster?.addToast({
        // NOTE: this feature is specific to Blend Title + Instant Title and would never appear for outside agencies
        message: `Workspace was unable to mark the loan as title ordered by the lender. <br/> Please reach out to #title-ops-issues in slack and provide the loan id.`,
        theme: 'error',
      });
    }
  };

  return (
    <>
      <div className={css(styles.orderConfirmationCard)}>
        <Text>Confirm that the lender has placed the order with {agencyName}.</Text>
        <ActionButton
          id="OrderConfirmation-openModal"
          label="Confirm Title Order"
          onClick={() => setIsModalOpen(true)}
          theme={'tertiary'}
        />
      </div>
      <Modal contentLabel="Confirm Title Order" handleClose={handleCloseModal} isOpen={isModalOpen}>
        <ModalContent handleClose={handleCloseModal} headerText="Confirm Title Order">
          <Text>Please confirm the lender has placed the title order. Once clicked, you cannot undo this action. </Text>
          <div className={css(styles.buttonGroup)}>
            <div className={css(styles.cancelButtonWrapper)}>
              <ActionButton
                id="OrderConfirmation-cancel"
                label="Cancel"
                onClick={handleCloseModal}
                theme={'tertiary'}
              />
            </div>
            <ActionButton
              disabled={isSubmitting}
              id="OrderConfirmation-confirm"
              isLoading={isSubmitting}
              label="Confirm Title Order"
              onClick={confirmTitleOrder}
              theme={'primary'}
            />
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};
